import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RootRoutes } from '@core/navigation';
import { ErrorDialogComponent } from '@core/error-handling/components';
import {
  ApiErrorResponse,
  ValidationApiErrorResponse,
  ErrorNotification,
  ErrorHandlingResult,
  ErrorSeverity,
  ErrorNotificationStrategy,
} from '../api-error.model';
import { ErrorMessageService } from '../error-message/error-message.service';
import { ToastService } from '../../toast/toast/toast.service';
import { ErrorMetadata } from '../error-interceptor';

@Injectable({
  providedIn: 'root',
})
export class ApiErrorHandlerService {
  private errorMessageService = inject(ErrorMessageService);
  private dialog = inject(MatDialog);
  private toastService = inject(ToastService);
  private router = inject(Router);

  handleHttpError(
    error: HttpErrorResponse,
    metadata?: ErrorMetadata
  ): ErrorHandlingResult {
    console.error('API Error:', error);
    console.log('Error details:', {
      status: error.status,
      error: error.error,
      isStructured: this.isStructuredApiError(error),
      metadata,
    });

    if (
      error.status === 503 &&
      metadata?.retriesExhausted &&
      metadata?.isIdempotent
    ) {
      return this.handleServerUnavailable();
    }

    const strategy = this.getErrorStrategy(error);

    switch (strategy.displayType) {
      case 'dialog':
        if (this.isStructuredApiError(error)) {
          return this.handleStructuredApiError(error.error);
        } else {
          return this.handleGenericErrorWithDialog(error);
        }

      case 'toast':
        return this.handleErrorWithToast(error, strategy);

      case 'none':
        return this.handleSpecialError(error);

      default:
        return this.handleGenericHttpError(error);
    }
  }

  private handleServerUnavailable(): ErrorHandlingResult {
    const currentUrl = this.router.url;

    this.router.navigate(RootRoutes.serverUnavailable, {
      queryParams: { returnUrl: currentUrl },
    });

    return {
      handled: true,
      notification: {
        type: 'error',
        title: 'Server nicht erreichbar',
        message:
          'Der Server ist vorübergehend nicht verfügbar. Sie werden zur Fehlerseite weitergeleitet.',
      },
      shouldRetry: false,
    };
  }

  handleStructuredApiError(apiError: ApiErrorResponse): ErrorHandlingResult {
    console.log('Handling structured API error:', apiError);

    if (this.isValidationError(apiError)) {
      return this.handleValidationError(apiError as ValidationApiErrorResponse);
    }

    let title: string;
    let message: string;

    if (apiError.key === 'bad.request' && apiError.message) {
      title = 'Fehler';
      message = apiError.message;
    } else if (this.errorMessageService.hasErrorMapping(apiError.key)) {
      const errorMapping = this.errorMessageService.getErrorMessage(
        apiError.key
      );
      title = errorMapping.title;
      message = errorMapping.message;
    } else {
      title = 'Fehler';
      message = `Ein Fehler ist aufgetreten (${apiError.key}).`;
    }

    const notification: ErrorNotification = {
      type: 'error',
      title,
      message,
      actionable: false,
    };

    console.log('Showing error dialog with notification:', notification);
    this.showErrorDialog(notification);

    return {
      handled: true,
      notification,
      shouldRetry: false,
    };
  }

  private handleValidationError(
    apiError: ValidationApiErrorResponse
  ): ErrorHandlingResult {
    console.log('Handling validation error:', apiError);

    const isMissingVehicleData = this.isMissingVehicleDataError(
      apiError.additionalInformation
    );

    let title: string;
    let message: string;
    let details: string[];

    if (isMissingVehicleData) {
      title = 'Fahrzeugdaten unvollständig';
      message = 'Folgende Fahrzeugdaten müssen ergänzt werden:';
      details = this.translateVehicleFields(apiError.additionalInformation);
    } else {
      const errorMapping = this.errorMessageService.getErrorMessage(
        apiError.key
      );
      title = errorMapping.title;
      message =
        apiError.key === 'bad.request' && apiError.message
          ? apiError.message
          : errorMapping.message;
      details = apiError.additionalInformation;
    }

    const notification: ErrorNotification = {
      type: 'error',
      title,
      message,
      details,
      actionable: true,
    };

    console.log('Showing validation error dialog:', notification);
    this.showErrorDialog(notification);

    return {
      handled: true,
      notification,
      shouldRetry: false,
    };
  }

  private handleGenericHttpError(
    error: HttpErrorResponse
  ): ErrorHandlingResult {
    let errorKey = 'server.error';

    switch (error.status) {
      case 400:
        errorKey = 'bad.request';
        break;
      case 401:
        errorKey = 'unauthorized';
        break;
      case 403:
        errorKey = 'forbidden';
        break;
      case 404:
        errorKey = 'not.found';
        break;
      case 500:
      default:
        errorKey = 'server.error';
        break;
    }

    const errorMapping = this.errorMessageService.getErrorMessage(errorKey);
    const notification: ErrorNotification = {
      type: 'error',
      title: errorMapping.title,
      message: errorMapping.message,
      actionable: !!errorMapping.userActionHint,
    };

    this.showErrorDialog(notification);

    return {
      handled: true,
      notification,
      shouldRetry: this.shouldRetry(errorKey),
    };
  }

  private showErrorDialog(notification: ErrorNotification): void {
    console.log('Opening error dialog with data:', {
      title: notification.title,
      message: notification.message,
      details: notification.details,
    });

    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      data: {
        title: notification.title,
        message: notification.message,
        details: notification.details,
      },
      disableClose: false,
      autoFocus: true,
      width: '400px',
      maxWidth: '90vw',
    });

    dialogRef.afterClosed().subscribe(() => {
      console.log('Error dialog closed');
    });
  }

  private isStructuredApiError(error: HttpErrorResponse): boolean {
    return (
      error.error &&
      typeof error.error === 'object' &&
      'key' in error.error &&
      'message' in error.error
    );
  }

  private isValidationError(apiError: ApiErrorResponse): boolean {
    return (
      Array.isArray(apiError.additionalInformation) &&
      apiError.additionalInformation.length > 0
    );
  }

  private isMissingVehicleDataError(additionalInfo: string[]): boolean {
    if (!additionalInfo || additionalInfo.length === 0) {
      return false;
    }

    const vehicleFields = [
      'vehicletype',
      'vehicleType',
      'model',
      'subModel',
      'manufacturer',
      'vehicleLocation zip',
      'vehicleLocation countrycode',
      'vehicleLocation.zip',
      'vehicleLocation.countryCode',
      'CurrentDamage',
      'OldDamage',
      'RepairedDamage',
      'make',
      'year',
      'vin',
      'licensePlate',
      'mileage',
      'fuel',
      'transmission',
      'bodyType',
      'engineSize',
      'engineType',
      'power',
      'registrationDate',
      'calculation costOfRepairNet',
    ];

    return additionalInfo.some((field) =>
      vehicleFields.some((vf) => field.includes(vf))
    );
  }

  private translateVehicleFields(fields: string[]): string[] {
    return fields.map((field) => this.translateVehicleField(field));
  }

  private translateVehicleField(field: string): string {
    const vehicleFieldTranslations: Record<string, string> = {
      make: 'Marke',
      model: 'Modell',
      year: 'Baujahr',
      vin: 'Fahrgestellnummer',
      licensePlate: 'Kennzeichen',
      mileage: 'Kilometerstand',
      fuel: 'Kraftstoff',
      transmission: 'Getriebe',
      bodyType: 'Karosserieform',
      engineSize: 'Hubraum',
      engineType: 'Motorart',
      mileageType: 'Laufleistung Einheit',
      power: 'Leistung',
      registrationDate: 'Erstzulassung',

      vehicletype: 'Fahrzeugtyp',
      vehicleType: 'Fahrzeugtyp',
      subModel: 'Untermodell',
      manufacturer: 'Hersteller',
      'vehicleLocation zip': 'PLZ des Fahrzeugstandorts',
      'vehicleLocation countrycode': 'Ländercode des Fahrzeugstandorts',
      'vehicleLocation.zip': 'PLZ des Fahrzeugstandorts',
      'vehicleLocation.countryCode': 'Ländercode des Fahrzeugstandorts',
      vehicleLocation: 'Fahrzeugstandort',
      CurrentDamage: 'Aktueller Schaden',
      OldDamage: 'Altschaden',
      RepairedDamage: 'Reparierter Schaden',
      'CurrentDamage or OldDamage and RepairedDamage':
        'Aktueller Schaden oder Altschaden und Reparierter Schaden',
      'calculation costOfRepairNet': 'Reparaturkosten Netto',
    };

    if (field.includes('.')) {
      if (vehicleFieldTranslations[field]) {
        return vehicleFieldTranslations[field];
      }

      const parts = field.split('.');
      const translatedParts = parts.map(
        (part) => vehicleFieldTranslations[part] || part
      );
      return translatedParts.join(' - ');
    }

    return vehicleFieldTranslations[field] || field;
  }

  private getErrorStrategy(
    error: HttpErrorResponse
  ): ErrorNotificationStrategy {
    if (error.status === 401) {
      return {
        severity: ErrorSeverity.WARNING,
        displayType: 'none',
        duration: 3000,
        actions: [
          {
            label: 'Zur Anmeldung',
            action: () => this.redirectToLogin(),
          },
        ],
      };
    }

    if (error.status === 400 || this.isStructuredApiError(error)) {
      return {
        severity: ErrorSeverity.CRITICAL,
        displayType: 'dialog',
      };
    }

    if ([402, 409, 422, 429].includes(error.status)) {
      return {
        severity: ErrorSeverity.CRITICAL,
        displayType: 'dialog',
      };
    }

    if ([403, 404].includes(error.status)) {
      return {
        severity: ErrorSeverity.WARNING,
        displayType: 'toast',
        duration: 5000,
      };
    }

    if (error.status >= 500) {
      return {
        severity: ErrorSeverity.TRANSIENT,
        displayType: 'toast',
        duration: 0,
        autoRetry: true,
        actions: [
          {
            label: 'Erneut versuchen',
            action: () => this.retryLastRequest(),
          },
        ],
      };
    }

    if (error.status === 0) {
      return {
        severity: ErrorSeverity.TRANSIENT,
        displayType: 'toast',
        duration: 0,
        autoRetry: true,
      };
    }

    return {
      severity: ErrorSeverity.INFO,
      displayType: 'toast',
      duration: 5000,
    };
  }

  private handleErrorWithToast(
    error: HttpErrorResponse,
    strategy: ErrorNotificationStrategy
  ): ErrorHandlingResult {
    let title: string;
    let message: string;

    if (this.isStructuredApiError(error)) {
      const apiError = error.error as ApiErrorResponse;
      const errorMapping = this.errorMessageService.getErrorMessage(
        apiError.key
      );
      title = errorMapping.title;
      message =
        apiError.key === 'bad.request' && apiError.message
          ? apiError.message
          : errorMapping.message;
    } else {
      const errorMapping = this.errorMessageService.getErrorMessage(
        this.getGenericErrorKey(error.status)
      );
      title = errorMapping.title;
      message = errorMapping.message;
    }

    let toastType: 'error' | 'warning' | 'info';
    switch (strategy.severity) {
      case ErrorSeverity.CRITICAL:
        toastType = 'error';
        break;
      case ErrorSeverity.WARNING:
        toastType = 'warning';
        break;
      default:
        toastType = 'info';
        break;
    }

    this.toastService.show({
      type: toastType,
      title,
      message,
      duration: strategy.duration ?? 5000,
      actions: strategy.actions,
    });

    const notification: ErrorNotification = {
      type: 'error',
      title,
      message,
      actionable: !!strategy.actions?.length,
    };

    return {
      handled: true,
      notification,
      shouldRetry: strategy.autoRetry ?? false,
    };
  }

  private handleSpecialError(error: HttpErrorResponse): ErrorHandlingResult {
    if (error.status === 401) {
      this.toastService.warning(
        'Sitzung abgelaufen',
        'Sie werden zur Anmeldung weitergeleitet...',
        3000
      );

      setTimeout(() => this.redirectToLogin(), 1000);
    }

    return {
      handled: true,
      notification: {
        type: 'warning',
        title: 'Session expired',
        message: 'Redirecting to login...',
      },
      shouldRetry: false,
    };
  }

  private handleGenericErrorWithDialog(
    error: HttpErrorResponse
  ): ErrorHandlingResult {
    const errorMapping = this.errorMessageService.getErrorMessage(
      this.getGenericErrorKey(error.status)
    );

    const notification: ErrorNotification = {
      type: 'error',
      title: errorMapping.title,
      message: errorMapping.message,
      actionable: false,
    };

    this.showErrorDialog(notification);

    return {
      handled: true,
      notification,
      shouldRetry: false,
    };
  }

  private getGenericErrorKey(status: number): string {
    switch (status) {
      case 400:
        return 'bad.request';
      case 401:
        return 'unauthorized';
      case 403:
        return 'forbidden';
      case 404:
        return 'not.found';
      case 429:
        return 'too.many.requests';
      case 500:
        return 'server.error';
      default:
        return 'unknown.error';
    }
  }

  private redirectToLogin(): void {
    this.router.navigate(RootRoutes.login, {
      queryParams: { returnUrl: this.router.url },
    });
  }

  private retryLastRequest(): void {
    console.log('Retry functionality not yet implemented');
  }

  private shouldRetry(errorKey: string): boolean {
    const retryableErrors = ['network.error', 'server.error', 'timeout.error'];

    return retryableErrors.includes(errorKey);
  }
}
